import React from 'react';
import PropTypes from 'prop-types';
import { Redirect } from 'react-router-dom';
import Button from '@material-ui/core/Button';
import DialogContent from '@material-ui/core/DialogContent';
import DialogActions from '@material-ui/core/DialogActions';
import Dialog from '@material-ui/core/Dialog';
import FormControlLabel from '@material-ui/core/FormControlLabel';
import Checkbox from '@material-ui/core/Checkbox';
import HTMLTourView from './HTMLTourView';
import HighlightAction, { DIALOG_CLASSNAME } from './HightlightAction';
import ClickAction from './ClickAction';
import OpenMenuAction from './OpenMenuAction';

import './TourDialog.scss';

const ActionFactories = {
  highlight: HighlightAction,
  click: ClickAction,
  openMenu: OpenMenuAction,
};

class TourDialog extends React.Component {
  static propTypes = {
    viewService: PropTypes.shape({
      getIcon: PropTypes.func.isRequired,
    }).isRequired,
    onClose: PropTypes.func.isRequired,
    configuration: PropTypes.shape({
      steps: PropTypes.arrayOf(PropTypes.shape({})),
      sections: PropTypes.arrayOf(PropTypes.shape({})),
    }).isRequired,
    open: PropTypes.bool.isRequired,
    location: PropTypes.shape({}),
  };

  static defaultProps = {
    location: undefined,
  }

  constructor(props) {
    super(props);

    this.locationToRestore = this.props.location;
    this.state = {
      step: 0,
      currentSection: 0,
      currentSectionStep: 0,
      dontShowAgain: false,
      redirectToInitLocation: false,
    };

    this.dialogRef = React.createRef();

    // Bindings
    this.handleGotIt = this.handleGotIt.bind(this);
    this.handleNextStep = this.handleNextStep.bind(this);
    this.handlePreviousStep = this.handlePreviousStep.bind(this);
    this.handleDontShowAgain = this.handleDontShowAgain.bind(this);
    this.handleClose = this.handleClose.bind(this);

    // Initialize steps and sections
    const { configuration, viewService } = props;
    const { steps, sections } = configuration;
    const sectionsByName = {};
    if (sections) {
      this.state.sections = sections.map((s) => {
        const section = {
          name: s.name,
          title: s.title,
          Icon: viewService.getIcon(s.icon),
          stepCount: 0,
        };
        sectionsByName[s.name] = section;
        return section;
      });
    }
    this.state.steps = steps.map((step) => {
      if (step.section) {
        sectionsByName[step.section].stepCount += 1;
      }
      return {
        ...step,
        actionPerfs: step.actions && step.actions.map(cfg => ActionFactories[cfg.type]) };
    });
  }

  componentDidMount() {
    this.startStepAnimations(this.state.step);
  }

  componentDidUpdate(prevProps) {
    if (this.props.open && !prevProps.open) {
      this.locationToRestore = this.props.location;
    } else if (!this.state.moved && (this.props.location !== this.locationToRestore)) {
      // Catch the automatic redirect at the start of the app if URL === '/'
      this.locationToRestore = this.props.location;
    }
  }

  handleClose() {
    this.close(this.state.dontShowAgain);
  }

  handleGotIt() {
    this.close(true);
  }

  handleNextStep() {
    this.goToStep(this.state.step + 1);
  }

  handlePreviousStep() {
    this.goToStep(this.state.step - 1);
  }

  handleGoToSection(section) {
    const { sections } = this.state;
    let step = 0;
    for (let iSection = 0; iSection < section; iSection += 1) {
      step += sections[iSection].stepCount;
    }
    this.goToStep(step);
  }

  calcSectionStep(step) {
    const { sections } = this.state;
    let currentSection = 0;
    let currentSectionStep = 0;
    if (sections) {
      for (let i = 0; i < step; i += 1) {
        currentSectionStep += 1;
        if (currentSectionStep === sections[currentSection].stepCount) {
          currentSectionStep = 0;
          currentSection += 1;
        }
      }
    }
    return { currentSection, currentSectionStep };
  }

  handleDontShowAgain() {
    const { dontShowAgain } = this.state;
    this.setState({ dontShowAgain: !dontShowAgain });
  }

  goToStep(newStep) {
    const { step } = this.state;
    this.stopStepAnimations(step).then(() => {
      this.setState({
        step: newStep,
        ...this.calcSectionStep(newStep),
        moved: true,
      }, () => {
        this.startStepAnimations(newStep);
      });
    });
  }

  close(gotIt) {
    const { step } = this.state;
    this.stopStepAnimations(step).then(() => {
      const hasLocationChanged = this.locationToRestore
            && (this.props.location !== this.locationToRestore);
      this.setState({
        moved: false,
        redirectToInitLocation: hasLocationChanged,
      }, () => {
        if (hasLocationChanged) {
          this.setState({ redirectToInitLocation: false });
        }
        this.props.onClose(gotIt);
      });
    });
  }

  startStepAnimations(step) {
    const stepObj = this.state.steps[step];
    if (stepObj.actionPerfs) {
      return stepObj.actionPerfs.reduce((promiseChain, action, i) => {
        return promiseChain.then((chainResults) => {
          const result = action.start(stepObj.actions[i], this.props);
          return result && result.then
                && result.then(currentResult => [...chainResults, currentResult])
                || [...chainResults, result];
        });
      }, Promise.resolve([])).then((results) => {
        stepObj.toRestore = results;
      })
      .catch((err) => { console.log(err); });
    }
    return Promise.resolve([]);
  }

  stopStepAnimations(step) {
    const stepObj = this.state.steps[step];
    if (stepObj.actionPerfs) {
      return stepObj.actionPerfs.slice().reverse().reduce((promiseChain, action, i) => {
        const realIndex = stepObj.actionPerfs.length - i - 1;
        return promiseChain.then((chainResults) => {
          const result = action.stop(stepObj.actions[realIndex], this.props, stepObj.toRestore[realIndex]);
          return Promise.resolve(result).then(currentResult => [currentResult, ...chainResults]);
        });
      }, Promise.resolve([]))
      .catch((err) => { console.log(err); });
    }
    return Promise.resolve([]);
  }

  render() {
    const { open } = this.props;
    const { step, steps, sections, currentSection, currentSectionStep, dontShowAgain } = this.state;
    const handleGoToSection = (i) => {
      return () => this.handleGoToSection(i);
    };
    if (this.state.redirectToInitLocation) {
      return <Redirect to={this.locationToRestore} />;
    }
    return (
      <Dialog
        disableBackdropClick
        aria-labelledby="tour-dialog-title"
        className={DIALOG_CLASSNAME}
        onClose={this.handleClose}
        open={open}
        maxWidth="xl"
        style={{ zIndex: '1350' }} // Ensure the tour dialog stay on front of user bar popup which zIndex === 1300
      >
        <DialogContent>
          <main ref={this.dialogRef}>
            {sections && (
              <ul className="grb-tour-dialog-sections">
                {sections.map((section, iSection) => (
                  <li
                    key={section.name}
                    className={iSection === currentSection ? 'active' : ''}
                  >
                    <div
                      onClick={handleGoToSection(iSection)}
                      onKeyPress={() => {}}
                      role="button"
                      tabIndex="0"
                    >
                      {section.Icon && <section.Icon />}
                      <span className="grb-tour-dialog-section-name">{section.title}</span>
                      {((section.stepCount > 1) && (iSection === currentSection)) ? (
                        <span className="grb-tour-dialog-section-step">{`${currentSectionStep + 1} / ${section.stepCount}`}</span>
                      ) : (
                        <span />
                      ) }
                    </div>
                  </li>
                ))}
              </ul>
            )}
            <HTMLTourView url={steps[step].page.url} title={steps[step].page.title} />
          </main>
        </DialogContent>
        <DialogActions>
          {(step === 0) && (
            <FormControlLabel
              control={(
                <Checkbox
                  checked={dontShowAgain}
                  onChange={this.handleDontShowAgain}
                />
              )}
              label="Don't show the tour again"
            />
          )}
          <div className="grb-flex" />
          {step ? (
            <React.Fragment>
              <Button variant="outlined" onClick={this.handlePreviousStep} color="primary">
                Previous
              </Button>
              {(step === steps.length - 1) ? (
                <Button variant="outlined" onClick={this.handleGotIt} color="primary">
                  Got It
                </Button>
              ) : (
                <Button variant="outlined" onClick={this.handleNextStep} color="primary">
                  Next
                </Button>
              )}
            </React.Fragment>
          ) : (
            <React.Fragment>
              <Button variant="outlined" onClick={this.handleClose} color="primary">
                Close
              </Button>
              <Button variant="outlined" onClick={this.handleNextStep} color="primary">
                Get started
              </Button>
            </React.Fragment>
          )}
        </DialogActions>
      </Dialog>
    );
  }
}

export default TourDialog;
